import * as React from "react";
import { Text, Button } from "@uikit";
import { Review } from "@kalabo/golfsavers-common";
import TrustPilot from "@images/trustpilot.svg";
import StarRating from "../../starRating";
import { graphql, useStaticQuery } from "gatsby";

interface IRProps {
  hideTitle?: boolean;
  sticky?: boolean;
  vertical?: boolean;
  hideButton?: boolean;
}

const ReviewSummary = ({
  vertical = false,
  hideTitle = false,
  sticky = false,
  hideButton = false,
}: IRProps) => {
  const static_query_results = useStaticQuery(graphql`
    query {
      allGolfsaversReviewsSummary {
        edges {
          node {
            id
            rating
            count
          }
        }
      }
    }
  `);

  let data = static_query_results.allGolfsaversReviewsSummary.edges.map(
    (edge: any) => {
      return {
        rating: edge.node.rating,
        count: edge.node.count,
      };
    }
  );

  let counts = static_query_results.allGolfsaversReviewsSummary.edges
    .map((edge: any) => {
      return {
        rating: edge.node.rating,
        count: edge.node.count,
      };
    })
    .sort((a: any, b: any) => b.rating - a.rating);

  let total = data.reduce(
    (acc: any, curr: { count: any }) => acc + curr.count,
    0
  );

  let total_ratings = data.reduce(
    (acc: any, curr: { count: any }) => acc + curr.count,
    0
  );

  // get the average rating based on the rating and the number of occurrences
  let average =
    data.reduce(
      (acc: any, curr: { rating: any; count: any }) =>
        acc + curr.rating * curr.count,
      0
    ) / total_ratings;

  const getRatingName = (rating: number) => {
    switch (rating) {
      case 1:
        return "Bad";
      case 2:
        return "Poor";
      case 3:
        return "Average";
      case 4:
        return "Great";
      case 5:
        return "Excellent";
      default:
        return "Unknown";
    }
  };

  return (
    <>
      {!hideTitle && (
        <Text size={17} leading={1.3} weight="semibold">
          Review Summary
        </Text>
      )}
      <div
        className={`lg:col-span-4 px-6 py-8 mt-[10px] border border-bordergrey2 rounded-lg overflow-hidden ${
          sticky && "sticky top-[20px] "
        }`}
      >
        <div>
          <div
            className={`flex flex-col  justify-center lg:justify-start items-center  mb-4 ${
              vertical ? "lg:flex-col gap-4" : "lg:flex-col gap-4"
            }`}
          >
            <div
              className={`flex justify-between w-full gap-2 items-center ${
                vertical ? "flex-col justify-center" : "flex-col lg:flex-row"
              }`}
            >
              <div className={`flex flex-1 items-center flex-col lg:flex-row`}>
                <Text
                  size={30}
                  leading={1.2}
                  weight="semibold"
                  color="black"
                  className="mr-3"
                >
                  {average.toFixed(1)} out of 5
                </Text>

                <div className="flex">
                  <StarRating rating={Number(average)} hideCount size="large" />
                </div>
              </div>
              {!vertical && (
                <div className="flex relative">
                  <TrustPilot className="h-6 w-auto relative" />
                </div>
              )}
            </div>
            <div
              className={`w-full flex flex-1 justify-center ${
                vertical ? "" : " sm:justify-start"
              }`}
            >
              <Text
                size={17}
                color="black80"
                weight="semibold"
                className="mt-2 sm:mt-0 flex "
              >
                <span className="font-semibold">
                  Based on {new Intl.NumberFormat("en-US").format(total)} user
                  reviews
                </span>
              </Text>
            </div>
            {vertical && <TrustPilot className="h-8" />}
          </div>
        </div>

        <div className="mt-6">
          <h3 className="sr-only">Review data</h3>

          <dl className="space-y-[10px]">
            {counts.map((count: { rating: number; count: number }) => (
              <div key={count.rating} className="flex items-center text-sm">
                <dt className="flex-1 flex items-center">
                  <Text
                    size={13}
                    leading={1.5}
                    color={"black90"}
                    className="w-16 p-0 mb-0"
                  >
                    {getRatingName(count.rating)}
                    <span className="sr-only"> star reviews</span>
                  </Text>
                  <div
                    aria-hidden="true"
                    className="ml-1 flex-1 flex items-center"
                  >
                    <div className="ml-3 relative flex-1">
                      <div className="h-3 bg-grey border border-grey " />
                      {count.count > 0 ? (
                        <div
                          className="absolute inset-y-0 bg-green border border-green"
                          style={{
                            width: `calc(${count.count} / ${total_ratings} * 100%)`,
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                </dt>
                <dd className="ml-3 w-10 tabular-nums">
                  <Text
                    size={13}
                    leading={1.5}
                    color={"black90"}
                    className="p-0 mb-0  text-right"
                  >
                    {Math.round((count.count / total_ratings) * 100)}%
                  </Text>
                </dd>
              </div>
            ))}
          </dl>
        </div>
        {!hideButton && (
          <div className="mt-10">
            <Button
              size="small"
              icon="plus"
              text="Write a review"
              color="green"
              onClick={() => {}}
            />
          </div>
        )}
      </div>
    </>
  );
};
export default ReviewSummary;
